import api from './api';

export interface CaptchaConfig {
  enabled: boolean;
  site_key: string;
  provider: string;
}

export interface CaptchaToken {
  token: string;
  timestamp: number;
}

type TokenListener = (token: string | null) => void;

class CaptchaService {
  private config: CaptchaConfig | null = null;
  private hcaptchaWidgetId: string | null = null;
  private tokenListener: TokenListener | null = null;
  private scriptPromise: Promise<void> | null = null;

  async getConfig(): Promise<CaptchaConfig> {
    if (this.config) {
      return this.config;
    }
    return this.refreshConfig();
  }

  /** Clear cache and refetch — call when mounting auth forms after Admin may have saved keys. */
  async refreshConfig(): Promise<CaptchaConfig> {
    this.config = null;
    try {
      const response = await api.get('/auth/captcha-config');
      this.config = response.data;
      return this.config!;
    } catch (error) {
      console.error('Error fetching CAPTCHA config:', error);
      const fallback: CaptchaConfig = {
        enabled: false,
        site_key: '',
        provider: 'hcaptcha',
      };
      this.config = fallback;
      return fallback;
    }
  }

  private isApiReady(): boolean {
    return typeof window !== 'undefined'
      && typeof window.hcaptcha?.render === 'function';
  }

  private waitUntilReady(timeoutMs = 8000): Promise<void> {
    if (this.isApiReady()) {
      return Promise.resolve();
    }

    return new Promise((resolve, reject) => {
      const started = Date.now();
      const timer = window.setInterval(() => {
        if (this.isApiReady()) {
          window.clearInterval(timer);
          // Prefer ready() when present; never hang if it is missing.
          const apiObj = window.hcaptcha;
          if (typeof apiObj.ready === 'function') {
            apiObj.ready(() => resolve());
          } else {
            resolve();
          }
          return;
        }
        if (Date.now() - started > timeoutMs) {
          window.clearInterval(timer);
          reject(new Error('hCaptcha failed to load'));
        }
      }, 50);
    });
  }

  /**
   * Load hCaptcha with explicit render mode.
   * Pass forceLoad=true when a site key is known (e.g. forced after API requires captcha)
   * even if cached config still says disabled.
   */
  async loadCaptchaScript(forceLoad = false): Promise<void> {
    if (typeof window === 'undefined') return;

    if (this.isApiReady()) {
      return this.waitUntilReady();
    }

    if (this.scriptPromise) {
      return this.scriptPromise;
    }

    const config = await this.getConfig();
    if (!forceLoad && (!config.enabled || !config.site_key)) {
      return;
    }

    this.scriptPromise = new Promise<void>((resolve, reject) => {
      const existing = document.querySelector('script[src*="hcaptcha"]');
      if (existing) {
        this.waitUntilReady()
          .then(resolve)
          .catch((err) => {
            this.scriptPromise = null;
            reject(err);
          });
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://js.hcaptcha.com/1/api.js?render=explicit';
      script.async = true;
      script.defer = true;

      script.onload = () => {
        this.waitUntilReady()
          .then(resolve)
          .catch((err) => {
            this.scriptPromise = null;
            reject(err);
          });
      };

      script.onerror = () => {
        this.scriptPromise = null;
        reject(new Error('Failed to load hCaptcha script'));
      };

      document.head.appendChild(script);
    });

    return this.scriptPromise;
  }

  setTokenListener(listener: TokenListener | null): void {
    this.tokenListener = listener;
  }

  async renderCaptcha(containerId: string, siteKey: string): Promise<string> {
    if (!this.isApiReady()) {
      throw new Error('hCaptcha not available');
    }

    // Remove previous widget if re-rendering into a new container
    if (this.hcaptchaWidgetId != null) {
      try {
        window.hcaptcha.remove(this.hcaptchaWidgetId);
      } catch {
        // ignore
      }
      this.hcaptchaWidgetId = null;
    }

    const widgetId = window.hcaptcha.render(containerId, {
      sitekey: siteKey,
      theme: 'light',
      size: 'normal',
      callback: (token: string) => {
        this.tokenListener?.(token);
      },
      'expired-callback': () => {
        this.tokenListener?.(null);
      },
      'error-callback': () => {
        this.tokenListener?.(null);
      },
    });

    this.hcaptchaWidgetId = widgetId;
    return widgetId;
  }

  async getCaptchaToken(): Promise<string | null> {
    if (!this.isApiReady() || this.hcaptchaWidgetId == null) {
      return null;
    }

    try {
      const token = window.hcaptcha.getResponse(this.hcaptchaWidgetId);
      return token || null;
    } catch (error) {
      console.error('Error getting CAPTCHA token:', error);
      return null;
    }
  }

  async resetCaptcha(): Promise<void> {
    if (!this.isApiReady() || this.hcaptchaWidgetId == null) {
      return;
    }

    try {
      window.hcaptcha.reset(this.hcaptchaWidgetId);
      this.tokenListener?.(null);
    } catch (error) {
      console.error('Error resetting CAPTCHA:', error);
    }
  }

  async executeCaptcha(): Promise<string> {
    if (!this.isApiReady() || this.hcaptchaWidgetId == null) {
      throw new Error('CAPTCHA not available');
    }

    return new Promise((resolve, reject) => {
      try {
        window.hcaptcha.execute(this.hcaptchaWidgetId!, {
          callback: (token: string) => {
            resolve(token);
          },
          'error-callback': () => {
            reject(new Error('CAPTCHA execution failed'));
          },
        });
      } catch (error) {
        console.error('Error executing CAPTCHA:', error);
        reject(error);
      }
    });
  }

  isCaptchaRequired(error: any): boolean {
    return error?.response?.data?.requires_captcha === true;
  }

  getCaptchaSiteKey(error: any): string | null {
    return error?.response?.data?.captcha_site_key || null;
  }
}

// Extend Window interface for hCaptcha
declare global {
  interface Window {
    hcaptcha: {
      ready?: (callback: () => void) => void;
      render: (container: string, options: Record<string, unknown>) => string;
      getResponse: (widgetId: string) => string;
      reset: (widgetId: string) => void;
      remove: (widgetId: string) => void;
      execute: (widgetId: string, options: Record<string, unknown>) => void;
    };
  }
}

export default new CaptchaService();
